import { shallowRef } from 'vue';
import type { RouteRecordRaw } from 'vue-router';

import { PAGES as ROOT_PAGES } from '@utils/constants';
import type { Module } from '@utils/model';

import { PAGES } from './constants';

import ModuleIcon from '~icons/mdi/car-3-plus';

const routes: RouteRecordRaw[] = [
	{
		path: '/autogrammetry',
		name: PAGES.HOME,
		component: () => import('./pages/index.vue'),
		meta: {
			// Checked synchronously in the router's global guard, so a direct
			// navigation cannot reach the component before the layout's
			// post-mount redirect fires.
			isAllowed: ({ canAccessMLOpsManager }) => canAccessMLOpsManager || ROOT_PAGES.MODULES,
			documentTitle: 'Autogrammetry',
			title: 'Autogrammetry',
			subtitle: 'Profiles',
		},
		children: [
			{
				path: 'coverage',
				name: PAGES.COVERAGE,
				// Not pages/Coverage: the repo's .gitignore has a bare `coverage`
				// entry, and git on a case-insensitive filesystem quietly ignores
				// the directory — the page would be missing from the repository
				// while still present locally.
				component: () => import('./pages/MeshCoverage/index.vue'),
				meta: {
					isAllowed: ({ canAccessMLOpsManager }) => canAccessMLOpsManager || ROOT_PAGES.MODULES,
					documentTitle: 'Autogrammetry / Coverage',
					title: 'Autogrammetry',
					subtitle: 'Mesh coverage',
				},
			},
			{
				// Under coverage, because that is the only list of templates and
				// the only place this is reached from.
				path: 'coverage/:cacheKey',
				name: PAGES.MESH,
				component: () => import('./pages/MeshCoverage/MeshDetail.vue'),
				props: true,
				meta: {
					isAllowed: ({ canAccessMLOpsManager }) => canAccessMLOpsManager || ROOT_PAGES.MODULES,
					documentTitle: 'Autogrammetry / Mesh',
					title: 'Autogrammetry',
					subtitle: 'Mesh template',
				},
			},
			{
				path: 'profiles/:profileId/golden',
				name: PAGES.GOLDEN_QUEUE,
				component: () => import('./pages/Golden/Queue.vue'),
				props: true,
				meta: {
					isAllowed: ({ canAccessMLOpsManager }) => canAccessMLOpsManager || ROOT_PAGES.MODULES,
					documentTitle: 'Autogrammetry / Ground truth',
					title: 'Autogrammetry',
					subtitle: 'Ground truth',
				},
			},
			{
				path: 'profiles/:profileId/golden/:instanceId',
				name: PAGES.GOLDEN_WIZARD,
				component: () => import('./pages/Golden/Wizard.vue'),
				props: true,
				meta: {
					isAllowed: ({ canAccessMLOpsManager }) => canAccessMLOpsManager || ROOT_PAGES.MODULES,
					documentTitle: 'Autogrammetry / Ground truth / Annotate',
					title: 'Autogrammetry',
					subtitle: 'Ground truth / Annotate',
				},
			},
			{
				path: 'profiles/:profileId',
				name: PAGES.DETAIL,
				component: () => import('./pages/Detail/index.vue'),
				props: true,
				meta: {
					documentTitle: 'Autogrammetry / Profile',
					title: 'Autogrammetry',
					subtitle: 'Profile',
				},
			},
			{
				path: '',
				name: PAGES.BROWSE,
				component: () => import('./pages/Browse/index.vue'),
				meta: {
					isAllowed: ({ canAccessMLOpsManager }) => canAccessMLOpsManager || ROOT_PAGES.MODULES,
					documentTitle: 'Autogrammetry / Profiles',
					title: 'Autogrammetry',
					subtitle: 'Profiles',
				},
			},
		],
	},
];

/**
 * Gated on `canAccessMLOpsManager` rather than a permission of its own: the audience is the same
 * image-processing operators, and a dedicated `autogrammetry_access` permission would need an
 * agent-core change. See docs/autogrammetry-migration.md.
 */
export const AUTOGRAMMETRY_MODULE: Module = {
	id: PAGES.HOME,
	link: { name: PAGES.BROWSE },
	title: 'Autogrammetry',
	icon: shallowRef(ModuleIcon),
	enabled: ({ canAccessMLOpsManager }) => canAccessMLOpsManager,
	routes,
};
